import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2, Image, AlertCircle, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { LOCAL_PNG_GOLD_FILTER, LOCAL_PNG_DARK_BLUE_FILTER } from '../../utils/circuitLayouts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CircuitLayoutManager = ({ onSelectLayout, selectedRaceId }) => {
  const [circuits, setCircuits] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { theme } = useTheme();

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCircuits = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/circuits`, {
        headers: getAuthHeader()
      });
      // Sort alphabetically by original filename
      const sortedCircuits = [...response.data].sort((a, b) =>
        (a.original_filename || a.filename || '').toLowerCase()
          .localeCompare((b.original_filename || b.filename || '').toLowerCase(), 'nl')
      );
      setCircuits(sortedCircuits);
    } catch (err) {
      console.error('Error fetching circuits:', err);
    }
  }, []);

  useEffect(() => {
    fetchCircuits();
  }, [fetchCircuits]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Alleen PNG, JPG of WEBP bestanden toegestaan');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/circuits/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setSuccess('Circuit layout succesvol geüpload!');
      fetchCircuits();
      
      // Clear the input
      event.target.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Fout bij uploaden');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (circuitId) => {
    if (!window.confirm('Weet je zeker dat je deze circuit layout wilt verwijderen?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/circuits/${circuitId}`, {
        headers: getAuthHeader()
      });
      setSuccess('Circuit layout verwijderd');
      fetchCircuits();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fout bij verwijderen');
    }
  };

  const handleSelectForRace = async (circuitId) => {
    if (!selectedRaceId) {
      setError('Selecteer eerst een race');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/admin/races/${selectedRaceId}/circuit-layout?circuit_id=${circuitId}`, {}, {
        headers: getAuthHeader()
      });
      setSuccess('Circuit layout toegewezen aan race');
      if (onSelectLayout) {
        onSelectLayout(circuitId);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fout bij toewijzen');
    }
  };

  const getImageFilter = () => {
    return theme === 'dark' ? LOCAL_PNG_GOLD_FILTER : LOCAL_PNG_DARK_BLUE_FILTER;
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="w-5 h-5" />
          Circuit Layouts Beheren
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <div className="space-y-2">
          <Label>Nieuwe Circuit Layout Uploaden</Label>
          <p className="text-xs text-muted-foreground">
            Afbeeldingen worden automatisch geconverteerd naar monochroom voor consistente styling.
            <br />
            <strong>Vereisten:</strong> Breedte 200-2000px, Hoogte 150-1500px
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={handleFileUpload}
              disabled={uploading}
              className="flex-1"
              data-testid="circuit-upload-input"
            />
            {uploading && (
              <span className="text-sm text-muted-foreground">Uploaden...</span>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-500">
            <Check className="w-4 h-4" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        {/* Circuits Grid */}
        <div className="space-y-2">
          <Label>Beschikbare Layouts ({circuits.length})</Label>
          {circuits.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nog geen circuit layouts geüpload. Upload een afbeelding hierboven.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {circuits.map((circuit) => (
                <div
                  key={circuit.id}
                  className="relative group border border-border rounded-lg p-2 bg-card hover:bg-secondary/50 transition-colors"
                >
                  <div className="aspect-video bg-secondary/30 rounded flex items-center justify-center overflow-hidden">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${circuit.url}`}
                      alt={circuit.original_filename}
                      className="max-w-full max-h-full object-contain"
                      style={{ filter: getImageFilter() }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate" title={circuit.original_filename}>
                    {circuit.original_filename}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {circuit.width}x{circuit.height}px
                  </p>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {selectedRaceId && (
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-6 w-6"
                        onClick={() => handleSelectForRace(circuit.id)}
                        title="Toewijzen aan geselecteerde race"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="destructive"
                      className="h-6 w-6"
                      onClick={() => handleDelete(circuit.id)}
                      title="Verwijderen"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="text-xs text-muted-foreground bg-secondary/30 p-3 rounded-lg">
          <strong>Hoe te gebruiken:</strong>
          <ol className="list-decimal ml-4 mt-1 space-y-1">
            <li>Upload een circuit layout afbeelding (wordt automatisch monochroom gemaakt)</li>
            <li>Ga naar de Races tab en selecteer een race</li>
            <li>Klik bij die race op "Circuit Layout" en kies een geüploade layout</li>
            <li>De layout verschijnt automatisch op de Kalender en Home pagina</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

export default CircuitLayoutManager;
